import { type MutableRefObject, type Ref, type RefCallback, useMemo } from 'react';

/**
 * Hook that merges multiple refs into a single ref callback.
 * Useful when you need to forward a ref to multiple places or combine internal and external refs.
 * Handles both function refs and object refs automatically.
 *
 * @template Instance - The type of the element instance the refs will point to.
 * @param {Array<Ref<Instance> | undefined>} refs - Array of refs to merge (can include function refs, object refs, or undefined).
 * @returns {RefCallback<Instance> | null} A single ref callback that calls all provided refs, or null if all refs are null/undefined.
 */
export function useMergeRefs<Instance>(
  refs: Array<Ref<Instance> | undefined>,
): RefCallback<Instance> | null {
  return useMemo(() => {
    if (refs.every((ref) => ref == null)) {
      return null;
    }

    return (value) => {
      refs.forEach((ref) => {
        if (typeof ref === 'function') {
          ref(value);
        } else if (ref != null) {
          (ref as MutableRefObject<Instance | null>).current = value;
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, refs);
}
